import {
  PAGE_SIZES,
  type PageSize,
  type RegisterExportQuery,
  type RegisterQuery,
  type TeamPeopleQuery,
  type TeamRecognitionQuery,
} from '@myrewards/contracts';
import { teamSearch, type Loose } from '../api/insights';

// Team recognition's address (SL8, APP-045 A18): the report's view (sector, time frame, award
// types) and the people list's (team, search, program, who, order, grouping, page, cells).
// Kept apart from the page so the route's loader reads it without loading the page's code.

export type Filter = 'all' | 'recognized' | 'not-yet' | 'repeat';
export type Cells = 'value' | 'count';

const FILTERS: Record<Filter, TeamPeopleQuery['recognized']> = {
  all: 'all',
  recognized: 'recognized',
  'not-yet': 'not_yet',
  repeat: 'repeat',
};
export const SORTS = ['recent', 'value', 'count', 'name'] as const;

/** The people list's part of the address: who to list, how, and which page. */
export interface ListView {
  team: string | undefined;
  q: string | undefined;
  programId: string | undefined;
  filter: Filter;
  sort: TeamPeopleQuery['sort'];
  group: boolean;
  /** 1-based; the list's own `usePaging` settles the size when the address has none. */
  page: number;
  size: PageSize | undefined;
  cells: Cells;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Reads the list's view from an address; anything that doesn't fit falls back to its default. */
export function teamListView(search: URLSearchParams): ListView {
  const filter = search.get('filter');
  const sort = search.get('sort');
  const team = search.get('team');
  const programId = search.get('programId');
  const q = (search.get('q') ?? '').trim().slice(0, 100);
  const page = Number(search.get('page'));
  const size = Number(search.get('size'));
  return {
    team: team !== null && (team === 'none' || UUID.test(team)) ? team : undefined,
    q: q === '' ? undefined : q,
    programId: programId !== null && UUID.test(programId) ? programId : undefined,
    filter: filter === 'not-yet' || filter === 'recognized' || filter === 'repeat' ? filter : 'all',
    sort: SORTS.find((x) => x === sort) ?? 'recent',
    group: search.get('group') !== 'off',
    page: Number.isInteger(page) && page >= 1 ? page : 1,
    size: PAGE_SIZES.find((x) => x === size),
    cells: search.get('cells') === 'count' ? 'count' : 'value',
  };
}

/** The page's address: the report's view, then the list's (defaults left out). */
export function pageSearch(view: Loose<TeamRecognitionQuery>, list: ListView): string {
  const params = new URLSearchParams(teamSearch(view));
  if (list.team !== undefined) params.set('team', list.team);
  if (list.q !== undefined) params.set('q', list.q);
  if (list.programId !== undefined) params.set('programId', list.programId);
  if (list.filter !== 'all') params.set('filter', list.filter);
  if (list.sort !== 'recent') params.set('sort', list.sort);
  if (!list.group) params.set('group', 'off');
  if (list.page > 1) params.set('page', String(list.page));
  if (list.size !== undefined) params.set('size', String(list.size));
  if (list.cells !== 'value') params.set('cells', list.cells);
  const out = params.toString();
  return out === '' ? '' : `?${out}`;
}

/** The people list's request for a view and the sector the report settled on. */
export function peopleQueryOf(
  view: TeamRecognitionQuery,
  list: ListView,
  sectorId: string,
): Loose<TeamPeopleQuery> {
  return {
    ...view,
    sectorId,
    team: list.team,
    q: list.q,
    programId: list.programId,
    recognized: FILTERS[list.filter],
    sort: list.sort,
    groupByTeam: list.group,
    page: list.page,
    ...(list.size === undefined ? {} : { size: list.size }),
  };
}

/** The sector's awards in the same time frame, every one (for the export). */
export function registerExportOf(
  view: TeamRecognitionQuery,
  sectorId: string,
): Loose<RegisterExportQuery> {
  return view.period === 'last_12_months'
    ? { sectorId, period: 'last_12_months' }
    : { sectorId, period: 'fiscal_year', fiscalYear: view.fiscalYear };
}

/** …and the first few, newest first, for the page. */
export function registerOf(view: TeamRecognitionQuery, sectorId: string): Loose<RegisterQuery> {
  return { ...registerExportOf(view, sectorId), size: 8 };
}
